/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { breadcrumbTrailContainerMobSX, breadcrumbTrailContainerSX, breadcrumbTrailParentContainerSX, scrollLeftBtnSx, scrollRightBtnSx } from '@/components/content/BreadcrumbTrail/styles/container';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { Box, Breadcrumbs, Stack, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { combineSX } from '@/utils/combineSX';
import { sideScroll } from '@/utils/sideScroll';
import { useDeviceType } from '@/data/deviceDetection';
import { breadCrumbStructuredData as defaultBreadCrumbStructuredData } from '@/data/constants/structuredData';
import { usePathname } from 'next/navigation';
import { useSettings } from '@/data/Settings';
import { DYNAMIC_CONTENT_PARENT_PAGE_SEO_URL } from '@/data/constants/pageDataFromId';

export const BreadcrumbTrail: FC<{ id: ID }> = () => {
	const { breadcrumb, uniqueId, displayPageName } = useBreadcrumbTrail();
	const path = usePathname();
	const breadCrumbNLS = useLocalization("BreadCrumb");
	const breadcrumbLength = breadcrumb?.length;
	const {isMobile} = useDeviceType();
	const { settings } = useSettings();
	
	const [showScroll, setShowScroll] = useState({ left: false, right: false });

	useEffect(() => {
		const breadcrumbTrail = document.getElementById("breadcrumb-trail") as HTMLElement | null;
		const bContainer = document.getElementById("breadcrumb-trail-container") as HTMLElement | null;

		if (!breadcrumbTrail || !bContainer || !isMobile) return;

		const updateScroll = () => {
			const maxScroll = breadcrumbTrail.scrollWidth - breadcrumbTrail.clientWidth;
			const left = breadcrumbTrail.scrollLeft > 30;
			const right = breadcrumbTrail.scrollLeft < maxScroll - 30;
			setShowScroll((prev) => {
				if (prev.left === left && prev.right === right) return prev;
				return { left, right };
			});
		};

		const bContainerWidth = bContainer.clientWidth;
		const breadcrumbTrailWidth = [...breadcrumbTrail.children].at(0)?.clientWidth ?? 0;

		if (breadcrumbTrailWidth > bContainerWidth) {
			updateScroll();
		} else {
			setShowScroll({ left: false, right: false });
		}

		breadcrumbTrail.addEventListener('scroll', updateScroll);
		updateScroll();

		return () => breadcrumbTrail.removeEventListener('scroll', updateScroll);
	}, [isMobile]);

	const scrollBreadcrumb = (direction:string) => {
		const breadcumbTrail = document.getElementById("breadcrumb-trail");
		if(breadcumbTrail){
			sideScroll(breadcumbTrail,{direction,speed:25,distance:150,step:20})
		}
	} 
	let breadCrumbData = [{
							"@type": "ListItem",
							"position": 1,
							"name": "Home",
							"item": "https://www.intertoys.nl/"
							}];
	let isCustServiceHierarchyPage = false;
	const basePath = useMemo(()=>settings.userData.SiteLink_DomainName,[settings]);
	
	if(breadcrumb?.length && ((breadcrumb?.at(0)?.label?.trim()?.toLocaleLowerCase()??"") in DYNAMIC_CONTENT_PARENT_PAGE_SEO_URL) ){   
		
		isCustServiceHierarchyPage = true;
	}

	const breadCrumbStructuredData = useMemo(() => {
        let breadCrumbStructuredDataFinal = defaultBreadCrumbStructuredData;
	   	for( let i=0, j=1; i<(breadcrumb?.length ?? 0); i++,j++){
				breadCrumbData.push({
					"@type": "ListItem",
					"position": j+1 ,
					"name": breadcrumb?.at(i)?.label?.trim() ?? "",
					//"item": breadcrumb?.[i]?.href?.trim() ?? ""
					"item": (i === (breadcrumb?.length??0) - 1 && isCustServiceHierarchyPage ) ? basePath+path : breadcrumb?.[i]?.href?.trim() ?? ""
				});				
	}
			breadCrumbStructuredDataFinal = breadCrumbStructuredDataFinal
				 	.replace("replaceBreadCrumbListItem",JSON.stringify(breadCrumbData));
         return breadCrumbStructuredDataFinal;
     }, [
        path,
        breadcrumb
     ]);

	return (
		<>
			<Stack sx={breadcrumbTrailParentContainerSX} id="breadcrumb-trail-container" direction="row">
				{isMobile && showScroll.left ? 
					<Box onClick={()=>scrollBreadcrumb("left")} sx={scrollLeftBtnSx}>
						<KeyboardArrowRightIcon  fontSize='small'/>
					</Box>
				:null}
				{breadcrumbLength ?
					<Breadcrumbs
						sx={combineSX([breadcrumbTrailContainerSX, isMobile ? breadcrumbTrailContainerMobSX : {}])}
						aria-label={breadcrumb?.at(-1)?.label}
						separator={
							<KeyboardArrowRightIcon fontSize='small' />
						}
						id="breadcrumb-trail"
					>
						<Linkable href="/">{breadCrumbNLS.Home.t()}</Linkable>
						{breadcrumb.map(({ href, label, trail }, index: number) => (
							<Linkable
								key={`breadcrumb-${uniqueId}-${index}-${label}`}
								href={index < breadcrumbLength - 1 ? href : undefined}
								type={index < breadcrumbLength - 1 ? 'link' : 'inline'}
								data-testid={`breadcrumb-${uniqueId}-${index}-${label}`}
								id={`breadcrumb-${uniqueId}-${index}-${label}`}
								tabIndex={index < breadcrumbLength - 1 ? 0 : -1}
								sx={{ pointerEvents: breadcrumbLength - 1 === index ? 'none' : 'unset' }}
							>
								{index < breadcrumbLength - 1 ? (
									label
								) : (
									<Typography variant="miniBody1">{label}</Typography>
								)}
							</Linkable>
						))}
					</Breadcrumbs> :
					<Breadcrumbs
						sx={breadcrumbTrailContainerSX}
						aria-label={breadcrumb?.at(-1)?.label}
						separator={<KeyboardArrowRightIcon fontSize='small' />}
					>
						<Linkable href="/">{breadCrumbNLS.Home.t()}</Linkable>
						{displayPageName ?
							<Typography variant="miniBody1"> {displayPageName}</Typography>
							: null}
					</Breadcrumbs>
				}
				{isMobile && showScroll.right && <Box onClick={()=>scrollBreadcrumb("right")} sx={scrollRightBtnSx}><KeyboardArrowRightIcon /></Box>}
			</Stack>
			{breadCrumbStructuredData ? (
				<script
					id="breadCrumbData"
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: breadCrumbStructuredData }}
				></script>
			) : null}
		</>
	) 
};